import { forwardRef, type ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';

import { Typography } from '@/modules/perseo-billing/ui/react/src/components/beta';

import type { ISelectProps } from './input.interface';
import type { FontSizesType } from './ui.interface';

const getInputClass = (
  disabled: boolean,
  error: ReactNode,
  color: 'success' | 'default'
) => {
  if (disabled) return 'disabled co-bg-gray-xlight co-border-disabled-main';
  if (error)
    return 'co-border-pds-error-500 focus:co-border-pds-error-500 focus:co-ring-pds-error-500';
  if (color === 'success')
    return 'co-border-dark-green focus:co-border-dark-green focus:co-ring-dark-green';
  return 'co-border-pds-neutral-300 focus:co-border-pds-primary-500 focus:co-border-2';
};

const renderError = (error: ReactNode, labelSize: FontSizesType) => {
  if (!error) return null;

  return (
    <div className="co-w-full">
      {typeof error === 'string' ? (
        <Typography size={labelSize ?? 'body1'} color="error">
          {error}
        </Typography>
      ) : (
        error
      )}
    </div>
  );
};

export const CardSelect = forwardRef<HTMLSelectElement, ISelectProps>(
  ({
    label,
    name,
    error,
    disabled = false,
    color = 'default',
    autocomplete = 'off',
    size = 'medium',
    value,
    labelSize = 'body2',
    ...props
  }: ISelectProps) => {
    const { register } = useFormContext();
    const inputClass = getInputClass(disabled, error, color);

    return (
      <div>
        <select
          autoComplete={autocomplete}
          disabled={disabled}
          className={`
                        ${inputClass}
                        co-input-text-${size}
                        co-px-16px
                        co-w-full
                        co-text-[14px]
                        co-h-[46px]
                        co-border-1
                        co-rounded-md
                        co-font-semibold
                        co-form-select
                        focus:outline-none
                        focus:co-ring-1
                        co-transition-all co-duration-300
                        `}
          value={value}
          defaultValue={''}
          id={name}
          {...register(name)}
        >
          <option value="" disabled>
            {label}
          </option>
          {props.options.map((option, index) => (
            <option key={index} value={option.value}>
              {option.name}
            </option>
          ))}
        </select>
        {renderError(error, labelSize)}
      </div>
    );
  }
);
